import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  accountActivation,
  forgotPassword,
  ISignupPayload,
  logoutAll,
  MFASigninPayload,
  PasswordSigninPayload,
  resendActivation,
  resetPassword,
  signin,
  signout,
  signup,
  SSoSigninPayload,
} from '../services/auth-service';
import { useGlobalMutation } from 'state/query-client/hooks';
import {
  ErrorResponse,
  useCustomToast,
} from './use-custom-toast/use-custom-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getLoginOption } from '../services/sso-service';
import { useConfig } from 'hooks/use-config';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';

/**
 * Authentication Mutations
 *
 * A collection of reusable hooks for handling authentication-related operations using `useGlobalMutation`.
 * These hooks manage server communication, error handling, and success feedback for various auth flows,
 * including sign-in, sign-out, password reset, and account activation.
 *
 * Hooks:
 * - `useSigninMutation`: Handles password or MFA-based sign-in logic with error messaging
 * - `useSignoutMutation`: Handles user sign-out
 * - `useAccountActivation`: Activates a newly registered user account with toast notifications
 * - `useForgotPassword`: Sends a password reset link to the user's email
 * - `useResetPassword`: Resets user password after verification
 * - `useResendActivation`: Resends account activation link to user's email
 * - `useLogoutAllMutation`: Logs out user from all devices
 *
 * Features:
 * - Generic mutation handling using a global mutation wrapper
 * - Custom toast notifications for success and error feedback
 * - Flexible payload typing for sign-in variants (password or MFA)
 * - Detailed error parsing and user-friendly messaging
 *
 * Example:
 * ```tsx
 * const { mutate: signin, errorDetails } = useSigninMutation<'password'>();
 * signin({ username, password });
 * ```
 *
 * @module authMutations
 */

export const useSigninMutation = <T extends 'password' | 'mfa_code' | 'social'>() => {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const [errorDetails, setErrorDetails] = useState({
    title: '',
    message: '',
  });

  const queryClient = useQueryClient();

  const mutation = useGlobalMutation({
    mutationKey: ['signin'],
    mutationFn: async (payload: PasswordSigninPayload | MFASigninPayload | SSoSigninPayload) =>
      signin<T>(payload, configurationSettings),
    onSuccess: () => {
      setErrorDetails({ title: '', message: '' });
      queryClient.invalidateQueries({ queryKey: ['getLanguages'] });
    },
    onError: (error: any) => {
      let errorObj = error;
      trackEvent(EVENTS.ERROR(error, {
        endpoint: configurationSettings.paths.authentication.token
      }))
      try {
        if (typeof error === 'string') {
          errorObj = JSON.parse(error);
        }
      } catch (e) {
        console.error('Error parsing error response:', e);
      }

      const isInvalidCredentials = (errorObj: any) => {
        return (
          (errorObj?.status === 401 && errorObj?.error?.error === 'invalid_username_password') ||
          (errorObj?.response?.status === 401 &&
            errorObj?.response?.data?.error === 'invalid_username_password') ||
          (errorObj?.response?.status === 400 &&
            errorObj?.response?.data?.error === 'invalid_grant')
        );
      };

      const isUserInactiveOrNotVerified = (errorObj: any) => {
        return (
          errorObj?.status === 400 &&
          errorObj?.error?.error === 'user_inactive_or_not_verified' &&
          errorObj?.error?.error_description === 'User is not active or verified'
        );
      };

      const isUserNotAgreedTermsAndConditions = (errorObj: any) => {
        return (
          errorObj?.status === 400 &&
          errorObj?.error?.error === 'user_not_agreed_terms_and_conditions' &&
          errorObj?.error?.error_description === 'User is not accepted the terms and conditions'
        );
      };

      //   {
      //     "error": "user_not_agreed_terms_and_conditions",
      //     "error_description": "User is not accepted the terms and conditions"
      // }

      if (isInvalidCredentials(errorObj)) {
        setErrorDetails({
          title: t('INVALID_CREDENTIALS'),
          message: t('EMAIL_PASSWORD_NOT_VALID'),
        });
      } else if (isUserInactiveOrNotVerified(errorObj)) {
        setErrorDetails({
          title: t('USER_INACTIVE_OR_NOT_VERIFIED'),
          message: t('USER_INACTIVE_OR_NOT_VERIFIED_DESCRIPTION'),
        });
      } else if (isUserNotAgreedTermsAndConditions(errorObj)) {
        setErrorDetails({
          title: t('USER_NOT_AGREED_TERMS_AND_CONDITIONS'),
          message: t('USER_NOT_AGREED_TERMS_AND_CONDITIONS_DESCRIPTION'),
        });
      } else {
        setErrorDetails({
          title: t('SOMETHING_WENT_WRONG'),
          message: t('PLEASE_TRY_AGAIN'),
        });
      }
    },
  });

  return {
    ...mutation,
    errorDetails,
  };
};

export const useSignoutMutation = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['signout'],
    mutationFn: () => signout(configurationSettings),
  });
};

export const useAccountActivation = () => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['accountActivation'],
    mutationFn: (data: { password: string; code: string; captchaCode: string }) =>
      accountActivation(data, configurationSettings),

    onSuccess: () => {
      showSuccessToast({
        description: t('ACCOUNT_ACTIVATED_SUCCESSFULLY'),
      });
    },
    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};

export const useForgotPassword = () => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['forgotPassword'],
    mutationFn: (data: { email: string; captchaCode?: string }) =>
      forgotPassword(data, configurationSettings),
    onSuccess: () => {
      showSuccessToast({
        description: t('RESET_PASSWORD_LINK_SENT_EMAIL'),
      });
    },
    onError: ({ error }: ErrorResponse) => {
      if (!error.isSuccess && error.errors['Email'] == 'Not_Allowed') {
        error.errors['Email'] = t('EMAIL_NOT_ALLOWED');
        showErrorToast(error);
        return;
      }
      showErrorToast(error);
    },
  });
};

export const useResetPassword = () => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['resetPassword'],
    mutationFn: (data: { code: string; password: string }) =>
      resetPassword(data, configurationSettings),
    onSuccess: () => {
      showSuccessToast({
        description: t('SUCCESSFULLY_SET_PASSWORD'),
      });
    },
    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};

export const useResendActivation = () => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['resendActivation'],
    mutationFn: (data: { userId: string }) => resendActivation(data, configurationSettings),
    onSuccess: () => {
      showSuccessToast({
        description: t('RESET_PASSWORD_LINK_SENT_EMAIL'),
      });
    },
    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};

export const useLogoutAllMutation = () => {
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['logoutAll'],
    mutationFn: () => logoutAll(configurationSettings),
  });
};

export const useGetLoginOptions = () => {
  const { configurationSettings } = useConfig();

  return useQuery({
    queryKey: ['loginOption'],
    queryFn: () => getLoginOption(configurationSettings),
  });
};

export const useSignup = () => {
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['signup', 'email'],
    mutationFn: (payload: ISignupPayload) => signup(payload, configurationSettings),
    onSuccess: () => {
      showSuccessToast({
        description: 'An email has been sent successfully to your account.',
      });
    },
    onError: ({ error }: ErrorResponse) => {
      trackEvent(EVENTS.ERROR(error, {
        endpoint: configurationSettings.paths.identifier.signup
      }));
      showErrorToast(error);
    },
  });
};
